/**
 * Format helpers — INR currency, date-fns-style relative formatting,
 * fast & timezone-aware (Asia/Kolkata default).
 */

const TZ = 'Asia/Kolkata';

export function formatINR(amount, opts = {}) {
  if (amount == null) return '';
  const n = Number(amount);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: opts.minDigits ?? 0,
    maximumFractionDigits: opts.maxDigits ?? 0,
  }).format(n);
}

export function formatDate(dateInput, opts = {}) {
  if (!dateInput) return '';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: TZ,
    weekday: opts.weekday ?? 'short',
    day: 'numeric',
    month: 'short',
    year: opts.year ? 'numeric' : undefined,
  }).format(d);
}

export function formatTime(dateInput) {
  if (!dateInput) return '';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

export function formatDateTime(dateInput) {
  if (!dateInput) return '';
  return `${formatDate(dateInput)} · ${formatTime(dateInput)}`;
}

export function formatRelative(dateInput) {
  if (!dateInput) return '';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = Date.now();
  const diffMs = d.getTime() - now;
  const diffMin = Math.round(diffMs / 60000);
  const diffH = Math.round(diffMs / 3600000);
  const diffD = Math.round(diffMs / 86400000);

  const rtf = new Intl.RelativeTimeFormat('en-IN', { numeric: 'auto' });
  if (Math.abs(diffMin) < 1) return 'now';
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  if (Math.abs(diffH) < 24) return rtf.format(diffH, 'hour');
  if (Math.abs(diffD) < 30) return rtf.format(diffD, 'day');
  return formatDate(d, { weekday: false });
}

export function pluralize(n, singular, plural = singular + 's') {
  return n === 1 ? singular : plural;
}

export function truncate(str, max = 80) {
  if (!str) return '';
  if (str.length <= max) return str;
  return str.slice(0, max - 1).trimEnd() + '…';
}

/** Compute % availability + return a text label */
export function availabilityLabel(available, total) {
  if (!total || total <= 0) return '';
  const pct = (available / total) * 100;
  if (available <= 0) return 'Sold out';
  if (pct < 10) return `Only ${available} left`;
  if (pct < 30) return 'Filling fast';
  return null;
}

/**
 * formatEventDate — short editorial date for cards/hero
 *   default:  "17 Apr"
 *   { short: true }: "17 Apr"
 *   { withDay: true }: "Sat, 17 Apr"
 *   { withYear: true } & date in past year: "17 Apr 2024"
 */
export function formatEventDate(dateInput, opts = {}) {
  if (!dateInput) return '';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-IN', {
    timeZone: TZ,
    weekday: opts.withDay ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    year: opts.withYear ? 'numeric' : undefined,
  }).format(d);
  return parts;
}

// ─── r7.10 (QA DEF-019): the event row's own calendar fields ────────────────────────────────────────────
// The API describes an event with `date` ('YYYY-MM-DD'), `time` / `end_time` ('HH:MM[:SS]', wall clock in the
// event's zone), `end_date` for a multi-day run, `duration_min` and `languages[]`. These helpers turn those into
// labels without ever building an instant, so the day and the clock the organizer typed are the day and the clock
// the buyer reads — in any browser time zone.
const EVENT_LANGUAGE_NAMES = { en: 'English', kn: 'Kannada', hi: 'Hindi', ta: 'Tamil', te: 'Telugu', ml: 'Malayalam', mr: 'Marathi', tu: 'Tulu', kok: 'Konkani', bn: 'Bengali', gu: 'Gujarati', pa: 'Punjabi', or: 'Odia', ur: 'Urdu', other: 'Other' };

/** '2026-10-30' → a Date at *local* midnight of that calendar day (never UTC-shifted). Null when not a date. */
export function calendarDay(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** '2026-10-30' → 'Fri 30 Oct'; with end_date '2026-11-01' → 'Fri 30 Oct – Sun 1 Nov'. */
export function formatCalendarDay(ymd, opts = {}) {
  const d = calendarDay(ymd);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-IN', {
    weekday: opts.withDay === false ? undefined : 'short',
    day: 'numeric',
    month: 'short',
    year: opts.withYear ? 'numeric' : undefined,
  }).format(d);
}
export function formatCalendarRange(fromYmd, toYmd, opts = {}) {
  const a = formatCalendarDay(fromYmd, opts);
  if (!a) return '';
  const b = toYmd && String(toYmd).slice(0, 10) !== String(fromYmd).slice(0, 10) ? formatCalendarDay(toYmd, opts) : '';
  return b ? `${a} – ${b}` : a;
}

/** '19:00:00' | '19:00' | '9:05' → '7:00 pm' / '9:05 am'. Empty for anything else. */
export function formatClock(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  if (!m) return '';
  const h = Number(m[1]); const mi = m[2];
  if (h > 23 || Number(mi) > 59) return '';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mi} ${h < 12 ? 'am' : 'pm'}`;
}

/** 120 → '2 h'; 150 → '2 h 30 min'; 45 → '45 min'. Falls back to end_time − time when duration_min is missing. */
export function formatDuration(minutes, { time, end_time } = {}) {
  let n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) {
    const a = /^(\d{1,2}):(\d{2})/.exec(String(time || '')); const b = /^(\d{1,2}):(\d{2})/.exec(String(end_time || ''));
    if (!a || !b) return '';
    n = (Number(b[1]) * 60 + Number(b[2])) - (Number(a[1]) * 60 + Number(a[2]));
    if (n <= 0) n += 24 * 60;   // an overnight show (22:00 → 01:00)
  }
  const h = Math.floor(n / 60); const m = n % 60;
  if (h && m) return `${h} h ${m} min`;
  if (h) return `${h} h`;
  return `${m} min`;
}

/** ['en','kn'] → 'English, Kannada'. Unknown codes pass through upper-cased; empty → ''. */
export function formatLanguages(codes) {
  const list = Array.isArray(codes) ? codes : (codes ? [codes] : []);
  return list.map((c) => EVENT_LANGUAGE_NAMES[String(c).toLowerCase()] || String(c).toUpperCase()).join(', ');
}
